import classNames from "classnames";
import { Horizontal } from "Components/Horizontal";
import { HorizontalNote } from "Components/HorizontalNote";
import { FormikValues, useFormikContext } from "formik";
import { VerticalRadio } from "Inputs/Radio";
import React from "react";
import { PaymentMethodOptions } from "SharedConstants";
import {
  BankAccountInputProps,
  CreditCardInputProps,
  PaymentInputProps,
} from "SharedConstants/Schema";
import { formatDollarsAndCents } from "Tools";
import { AgentBillingInput } from "./AgentBillingInput";
import { BankAccountInput } from "./BankAccountInput";
import { CreditCardAndExpDateInput } from "./CreditCardAndExpDateInput";

export function isAgencyBillingSelected(
  props: PaymentInputProps,
  values: FormikValues,
) {
  return (
    props.AgentBillingInputProps &&
    [undefined, PaymentMethodOptions.AgencyBilling].includes(
      values.PaymentMethod,
    )
  );
}

export function hasPaymentTypeBeenSelected(
  props: PaymentInputProps,
  values: FormikValues,
) {
  return Object.values(props.PaymentOptions).includes(values.PaymentType);
}

export function isBankAccountSelected(
  props: PaymentInputProps,
  values: FormikValues,
) {
  return values.PaymentType === props.PaymentOptions.BankAccount;
}

export function PaymentInput(
  props: PaymentInputProps & {
    renderCCFeeNote?: CCFeeNoteRenderer;
  },
) {
  const { values } = useFormikContext<Record<string, unknown>>();
  const paymentTypeOptions = [
    [
      props.PaymentOptions.BankAccount,
      {
        label: (
          <span
            className={classNames({
              grayedOut: !props.BankAccountInputProps,
            })}
          >
            Checking or Savings Account
          </span>
        ),
        inputProps: { disabled: !props.BankAccountInputProps },
      },
    ],
    [props.PaymentOptions.CreditCard, "Credit Card"],
  ] as [string, string][];
  return (
    <>
      {props.AgentBillingInputProps && (
        <AgentBillingInput
          {...props.AgentBillingInputProps}
          fieldName="PaymentMethod"
          isBankPaymentAllowed={!!props.BankAccountInputProps}
        />
      )}
      {!isAgencyBillingSelected(props, values) && (
        <>
          <Horizontal isRequired label="Payment Method" shouldNotPadLabel>
            <div>
              <div style={{ marginBottom: "0.5em" }}>
                Please select a payment method below:
                {!props.BankAccountInputProps && (
                  <div className="is-italic smallNote">
                    {props.CustomNoteForBankPaymentDisallowed || (
                      <>
                        NOTE: Payment from a checking or savings account is not
                        available for coverage expiring in less than two (2)
                        weeks.
                      </>
                    )}
                  </div>
                )}
              </div>
              <VerticalRadio
                fieldName="PaymentType"
                options={paymentTypeOptions}
                value={
                  !props.BankAccountInputProps
                    ? props.PaymentOptions.CreditCard
                    : undefined
                }
              />
            </div>
          </Horizontal>
          <PaymentNotesAndInput {...props} />
        </>
      )}
    </>
  );
}

function PaymentNotesAndInput(
  props: PaymentInputProps & {
    renderCCFeeNote?: CCFeeNoteRenderer;
  },
) {
  const producerRole = "program administrator";
  const { values } = useFormikContext<Record<string, unknown>>();
  switch (values.PaymentType) {
    case props.PaymentOptions.BankAccount:
      return !props.BankAccountInputProps ? (
        <></>
      ) : (
        <BankAccountNotesAndInput
          {...props.BankAccountInputProps}
          TotalCost={props.TotalCost}
          merchantName={props.MerchantName}
          producerRole={producerRole}
        />
      );
    case props.PaymentOptions.CreditCard:
      return (
        <CreditCardNotesAndInput
          {...props.CreditCardInputProps}
          TotalCost={props.TotalCost}
          renderCCFeeNote={props.renderCCFeeNote}
          merchantName={props.MerchantName}
          producerRole={producerRole}
        />
      );
    default:
      // when payment type is blank or unrecognized, display nothing
      return <></>;
  }
}

type CCFeeNoteRenderer = (props: CreditCardInputProps) => React.ReactNode;

function CreditCardNotesAndInput(
  props: CreditCardInputProps & {
    TotalCost: number;
    renderCCFeeNote?: CCFeeNoteRenderer;
    merchantName: string;
    producerRole: string;
  },
) {
  return (
    <>
      <Horizontal>
        <span>
          Total Payment:{" "}
          <span className="has-text-weight-bold">
            {formatDollarsAndCents(props.TotalCost + props.CCFee)}
          </span>
          <span> {props.renderCCFeeNote && props.renderCCFeeNote(props)}</span>
        </span>
      </Horizontal>
      <HorizontalNote>
        Credit Card Types Accepted: Visa, MasterCard, Discover, American Express
      </HorizontalNote>
      <CreditCardAndExpDateInput {...props} />
      <ChargeNote
        merchantName={props.merchantName}
        producerRole={props.producerRole}
        accountType="credit card"
      />
    </>
  );
}

function BankAccountNotesAndInput(
  props: BankAccountInputProps & {
    TotalCost: number;
    merchantName: string;
    producerRole: string;
  },
) {
  return (
    <>
      <Horizontal>
        <span>
          Total Payment:{" "}
          <span className="has-text-weight-bold">
            {formatDollarsAndCents(props.TotalCost)}
          </span>
        </span>
      </Horizontal>
      <HorizontalNote>
        Enter your financial institution’s routing number and your U.S. checking
        or savings account number. You can find these numbers on the bottom of
        your checks, on a deposit slip, or by contacting your financial
        institution.
      </HorizontalNote>
      <ChargeNote
        merchantName={props.merchantName}
        producerRole={props.producerRole}
        accountType="bank account"
      />
      <BankAccountInput {...props} />
    </>
  );
}

function ChargeNote(props: {
  merchantName: string;
  producerRole: string;
  accountType: string;
}) {
  return (
    <HorizontalNote>
      Please note: The charge on your {props.accountType} will appear as{" "}
      <span className="has-text-weight-bold">{props.merchantName}</span>, the{" "}
      {props.producerRole}.
    </HorizontalNote>
  );
}
